import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft, Clock } from "lucide-react";
import { AutocompleteCustom } from "@/components/core/map/autocomplete";
import { RecentRecipientsSection } from "@/components/recent-recipients-section";
import { useUserLocation } from "@/hooks/useUserLocation";
import { EnderecoService } from "@/services/endereco.service";

export const Route = createFileRoute("/app/entregas/select-destination")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: (search.type as "send" | "receive") || "send",
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { type } = Route.useSearch();
  const { address: userAddress } = useUserLocation();

  const [recentRecipients, setRecentRecipients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const snapshotsResponse = await EnderecoService.findAll();

      const recipients = snapshotsResponse.map((snapshot: any) => ({
        id: snapshot.id,
        name: snapshot.nomeContato,
        address: `${snapshot.rua}, ${snapshot.numero || "S/N"} - ${snapshot.bairro}, ${snapshot.cidade}`,
        phone: snapshot.telefone,
        fullData: snapshot,
      }));

      setRecentRecipients(recipients);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationSelect = async (response: any) => {
    // Navigate to origin confirmation with selected destination
    const feature = response.features[0];
    const [lng, lat] = feature.geometry.coordinates;

    navigate({
      to: "/app/entregas/confirm-origin",
      search: {
        type,
        destinationLat: lat,
        destinationLng: lng,
        destinationAddress: feature.properties.full_address,
        destinationName: undefined,
        destinationPhone: undefined,
        favoritoId: undefined,
        snapshotId: undefined,
      },
    });
  };

  const handleRecentRecipientSelect = (recipient: any) => {
    navigate({
      to: "/app/entregas/confirm-origin",
      search: {
        type,
        destinationLat: recipient.fullData.latitude,
        destinationLng: recipient.fullData.longitude,
        destinationAddress: recipient.address,
        destinationName: recipient.name,
        destinationPhone: recipient.phone,
        favoritoId: undefined,
        snapshotId: recipient.id,
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate({ to: "/app" })}
          className="p-1 hover:bg-slate-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold">Qual o destino?</h1>
      </div>

      {/* Scheduling option */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Clock className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium">Ir agora</span>
        <select className="ml-auto text-sm border-0 bg-transparent focus:outline-none">
          <option>Ir agora</option>
          <option>Agendar</option>
        </select>
      </div>

      {/* Origin (pre-filled) */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 mb-1">Origem</p>
            <p className="text-sm font-medium text-slate-900">
              {userAddress || "Carregando sua localização..."}
            </p>
          </div>
        </div>
      </div>

      {/* Destination Autocomplete */}
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <div className="w-2 h-2 rounded-full border-2 border-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 mb-2">Destino</p>
            <AutocompleteCustom
              onLocationSelect={handleLocationSelect}
              placeholder="Escolha a localização do destinatário"
            />
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-slate-500">
            Carregando...
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <RecentRecipientsSection
              recipients={recentRecipients}
              onSelect={handleRecentRecipientSelect}
            />
          </div>
        )}
      </div>
    </div>
  );
}
